import React, { useRef, useEffect, useState } from 'react';
import TimerPopup from '@nile/components/src/Containers/NewWorklog/TimerPopup';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import {
  AccountClient,
  TicketClient,
  UPDATE_TICKET,
  GET_FIELD,
  GET_ALL_TIMER_ENTRIES
} from '@nile/graphql';
import {
  getCurrentUserHook,
  useIsAuthorisedHook,
  ALL_MODULES,
  DEFAULT_FIELDS_BY_COLUMN_NAME,
  EventEmitter,
  generateString,
  DATE_TIME_FORMAT,
  useCheckFeatureGating
} from '@nile/components';
import { omit, pick } from 'lodash';
import dayjs from 'dayjs';
import { useCheckIsItProduct } from '@nile/components/src/Utils/helpers';
import { useFlags } from 'launchdarkly-react-client-sdk';

function ChatTimer({
  threadData,
  timerPopupData,
  handleWorklogCreated,
  conversationStartTime,
  ...props
}) {
  const timerPopupRef = useRef();
  const currentUser = getCurrentUserHook();
  const isFeatureAuthorised = useIsAuthorisedHook('tickets.edit');
  const [isTicketClose, setIsTicketClose] = useState(true);
  const contractsNew = useCheckFeatureGating('CONTRACT_V3')?.enabled;

  const isItProduct = useCheckIsItProduct();

  const flags = useFlags();

  function closePopover() {
    timerPopupRef.current.togglePopover({});
  }

  const [fetchTimers] = useLazyQuery(GET_ALL_TIMER_ENTRIES, {
    fetchPolicy: 'network-only',
    variables: {
      isItProduct,
      worklogTimerInput: {
        listInfo: {
          condition: {
            joinOperator: 'AND',
            operands: [
              {
                attribute: 'technician.userId',
                operator: 'is',
                value: currentUser?.userId
              },
              {
                attribute: 'workItem.workId',
                operator: 'is',
                value: threadData?.workItem?.workId
              }
            ]
          }
        }
      }
    },
    onCompleted(res) {
      const runningTimer = res?.fetchWorklogTimerEntries?.entries.filter(
        (timer) => timer?.running
      )?.[0];
      if (res?.fetchWorklogTimerEntries?.entries?.length && runningTimer) {
        timerPopupRef?.current?.togglePopover({
          currentTimer: { ...runningTimer }
        });
      } else {
        const segmentStartTime = dayjs(conversationStartTime()).format(
          DATE_TIME_FORMAT
        );
        const segmentEndTime = dayjs(new Date()).format(DATE_TIME_FORMAT);
        timerPopupRef?.current?.togglePopover({
          currentTimer: {
            workItem: {
              ...timerPopupData,
              workId: threadData?.workItem?.workId,
              module: 'TICKET'
            },
            segments: [
              {
                segmentId: `${generateString(5)}`,
                startTime: segmentStartTime,
                endTime: segmentEndTime,
                timespent: segmentStartTime
                  ? dayjs(segmentEndTime).diff(dayjs(segmentStartTime)) /
                    (60 * 60 * 1000)
                  : '',
                afterHours: false
              }
            ]
          },
          onlyManual: true
        });
      }
    }
  });

  const [updateTicket] = useMutation(UPDATE_TICKET, {
    client: TicketClient
  });

  const { data: ticketStatusList } = useQuery(GET_FIELD, {
    client: AccountClient,
    variables: {
      input: {
        module: ALL_MODULES.ticket,
        columnName: DEFAULT_FIELDS_BY_COLUMN_NAME.STATUS
      }
    },
    skip: !isFeatureAuthorised
  });

  const closeTicket = (variables, dta) => {
    updateTicket({
      variables: {
        id: threadData?.workItem?.workId,
        ...variables
      }
    })
      .then((res) => {
        if (res?.data?.updateTicket) {
          EventEmitter.dispatch(
            'globalTicketClosedSuccesfully',
            res?.data?.updateTicket
          );
        }
      })
      .catch((err) => {});
  };

  function initiateCloseTicket(dta) {
    sessionStorage.setItem('closeTicketClicked', true);
    closePopover();
    if (isTicketClose) {
      const fldOptions =
        ticketStatusList?.getField?.options ||
        ticketStatusList?.getField?.fieldOptions;

      var stat = fldOptions?.find(
        (item) => item?.details?.constants === 'CLOSE'
      );
      closeTicket({ status: stat.value }, dta);
    }

    const workLog = dta || dta?.[0] || {};

    if (handleWorklogCreated) {
      handleWorklogCreated({
        worklog:
          {
            ...pick(workLog, [
              ...[contractsNew ? 'serviceCatalogItem' : 'serviceItem'],
              'billable',
              'billDateTime',
              'notes',
              'afterHours',
              'addSegments'
            ])
          } || null,
        ticket: null,
        closeTicket: false,
        createWorklog: !workLog?.itemId
      });
    }
  }

  const showWorklogTimer = () => {
    fetchTimers();
  };

  useEffect(() => {
    EventEmitter.subscribe('globalTicketClosedSuccesfully ', () => {
      setIsTicketClose(false);
      showWorklogTimer();
    });
    EventEmitter.subscribe('closeTicketAndConvertWorklog', (dta) => {
      setIsTicketClose(true);
      showWorklogTimer(dta);
    });
    EventEmitter.subscribe('closeSessionAndConvertWorklog', (dta) => {
      setIsTicketClose(false);
      showWorklogTimer(dta);
    });
    return function cleanup() {
      EventEmitter.unsubscribe('globalTicketClosedSuccesfully');
      EventEmitter.unsubscribe('closeTicketAndConvertWorklog');
      EventEmitter.unsubscribe('closeSessionAndConvertWorklog');
    };
  }, []);

  return (
    <TimerPopup
      refFromProps={timerPopupRef}
      handleWorklogCreated={initiateCloseTicket}
      isAgentChat
      threadData={threadData}
      disableSaveTimer
      hideDelete
      autoFocusTimerNotes
    >
      {props?.children}
    </TimerPopup>
  );
}

export default ChatTimer;
